import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
    Grid,
    Paper,
    Typography,
    Box,
    CircularProgress,
    Avatar,
    Chip,
    useTheme,
    Card,
    CardContent,
    Button,
    Stack,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    CardMembership as MembershipIcon,
    CheckCircle as CheckCircleIcon,
    Warning as WarningIcon,
    EventAvailable as BookIcon,
    Receipt as BillingIcon,
    History as HistoryIcon,
    ContactSupport as SupportIcon,
    QrCode as QrCodeIcon,
    AccountBox as SelfServiceIcon,
    People as PeopleIcon,
    SportsSoccer as SportsSoccerIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useResponsive } from '../hooks/useResponsive';
import { isMembershipValid } from '../utils/membershipUtils';
import api from '../services/api';
import Tooltip from '@mui/material/Tooltip';
import ExternalLinkDialog from '../components/common/ExternalLinkDialog';
import DashboardPopup from '../components/popups/DashboardPopup';
import PlayerCoachConfirmation from '../components/popups/PlayerCoachConfirmation';
import ContactPopup from '../components/popups/ContactPopup';

const QuickActionButton = ({ icon, label, onClick, color = 'primary', disabled, tooltipText }) => {
    const button = (
        <Button
            variant="contained"
            color={color}
            startIcon={icon}
            onClick={onClick}
            fullWidth
            disabled={disabled}
            sx={{
                py: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                textAlign: 'left',
                opacity: disabled ? 0.7 : 1,
                '& .MuiButton-startIcon': {
                    mr: 2,
                }
            }}
        >
            {label}
        </Button>
    );

    return tooltipText ? (
        <Tooltip title={tooltipText} arrow placement="top">
            <span style={{ width: '100%' }}>{button}</span>
        </Tooltip>
    ) : button;
};

const Dashboard = () => {
    const { user, loading: authLoading, refreshUser } = useAuth();
    const theme = useTheme();
    const { isMobile } = useResponsive();
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [announcementOpen, setAnnouncementOpen] = useState(!sessionStorage.getItem('announcementShown'));
    const [coachingDialogOpen, setCoachingDialogOpen] = useState(false);
    const [coachingLoading, setCoachingLoading] = useState(false);
    const [success, setSuccess] = useState(null);
    const [clientData, setClientData] = useState(null);
    const [children, setChildren] = useState([]);
    const isFromLogin = useRef(sessionStorage.getItem('justLoggedIn') === 'true');
    const [contactDialogOpen, setContactDialogOpen] = useState(false);
    const [settings, setSettings] = useState({});

    useEffect(() => {
        if (announcementOpen) {
            sessionStorage.setItem('announcementShown', 'true');
        }
    }, [announcementOpen]);

    // Force refresh auth data on mount, except when coming directly from login
    useEffect(() => {
        if (!isFromLogin.current) {
            //refreshUser();
            console.log("Saving refreshUser call");
        } else {
            sessionStorage.removeItem('justLoggedIn');
        }
    }, []); // Empty dependency array means this runs once on mount

    // Fetch settings
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api.get('/settings');
                setSettings(response.data.data);
            } catch (error) {
                console.error('Failed to fetch settings:', error);
            }
        };
        fetchSettings();
    }, []);

    // Fetch client data from our database
    useEffect(() => {
        const fetchClientData = async () => {
            try {
                const response = await api.get('/users/dashboard');
                const { client, children: childrenData } = response.data.data;
                setClientData(client);
                setChildren(childrenData || []);
            } catch (error) {
                setError('Failed to fetch dashboard data');
            } finally {
                setLoading(false);
            }
        };

        if (user?.ssUsername) {
            fetchClientData();
        }
    }, [user?.ssUsername]);

    // Get coaching/playing status from our database instead of custom fields
    const isCoachingOrPlaying = useMemo(() => {
        if (!clientData) return false;
        return clientData.isCoachingOrPlaying || false;
    }, [clientData]);

    const showCoachingButton = useMemo(() => {
        return !isCoachingOrPlaying && children.length > 0;
    }, [isCoachingOrPlaying, children]);

    const checkMembershipValidity = () => {
        const membershipType = user?.membershipType?.toLowerCase() || '';
        const numChildren = children.length;
        const requiredCount = numChildren === 0 ? 1 : (isCoachingOrPlaying ? numChildren + 1 : numChildren);

        // Basic checks for membership existence
        if (!user?.membershipNumber || !user?.membershipType || !user?.membershipClassification) {
            return {
                isValid: false,
                paidCount: 0,
                requiredCount: requiredCount
            };
        }

        // Check if membership is valid (contract status + date range)
        if (!isMembershipValid(user)) {
            return {
                isValid: false,
                paidCount: 0,
                requiredCount: requiredCount
            };
        }

        // If no children, need one membership for self
        if (numChildren === 0) {
            if (membershipType.includes('coach') || membershipType.includes('player') || membershipType.includes('administrator')) {
                return {
                    isValid: true,
                    paidCount: 1,
                    requiredCount: 1
                };
            }
            return {
                isValid: false,
                paidCount: 0,
                requiredCount: 1
            };
        }

        // With children, need player memberships matching total required count
        if (membershipType.includes('player')) {
            // Extract the number of players from the membership type (e.g., "3 Players (2025)" -> 3)
            const playerCount = parseInt(membershipType.match(/^(\d+)\s*player/)?.[1] || 1);
            
            return {
                isValid: playerCount >= requiredCount,
                paidCount: playerCount,
                requiredCount: requiredCount
            };
        }

        // Need player memberships for all required slots
        return {
            isValid: false,
            paidCount: 0,
            requiredCount: requiredCount
        };
    };

    const membershipStatus = checkMembershipValidity();
    const hasActiveMembership = membershipStatus.isValid;

    const getMembershipStatusText = () => {
        if (membershipStatus.isValid) {
            if (membershipStatus.paidCount > membershipStatus.requiredCount) {
                return `Administrative Fees Paid (${membershipStatus.paidCount}/${membershipStatus.requiredCount})`;
            }
            return 'All Administrative Fees Paid';
        }
        return `Administrative Fees Unpaid (${membershipStatus.paidCount}/${membershipStatus.requiredCount})`;
    };

    const handleEntryPassClick = () => {
        if (hasActiveMembership) {
            navigate('/entry-pass');
        } else {
            setDialogOpen(true);
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    const handleDialogConfirm = () => {
        setDialogOpen(false);
        navigate('/fees');
    };

    const handleSetCoachingPlaying = async (data) => {
        try {
            setCoachingLoading(true);
            setError(null);
            setSuccess(null);

            // Refresh dashboard data
            const dashboardResponse = await api.get('/users/dashboard');
            const { client, children: childrenData } = dashboardResponse.data.data;
            setClientData(client);
            setChildren(childrenData || []);
            
            setSuccess(data.message);
            setCoachingDialogOpen(false);
        } catch (error) {
            setError(error.response?.data?.message || 'An error occurred');
        } finally {
            setCoachingLoading(false);
        }
    };

    const quickActions = [
        {
            icon: <QrCodeIcon />,
            label: 'Entry Pass',
            onClick: handleEntryPassClick,
            color: 'success',
            disabled: false,
            tooltipText: !hasActiveMembership ? 'Please pay your administrative fees to access the facility' : ''
        },
        {
            icon: <PeopleIcon />,
            label: 'Manage Children',
            onClick: () => navigate('/children'),
            color: 'primary'
        },
        {
            icon: <SelfServiceIcon />,
            label: 'Self Service',
            onClick: () => window.open('https://totalturf.ezfacility.com/', '_blank'),
            color: 'primary'
        },
        {
            icon: <SupportIcon />,
            label: 'Contact TTE',
            onClick: () => setContactDialogOpen(true),
            color: 'secondary'
        }
    ];

    const handleAnnouncementClose = () => {
        sessionStorage.setItem('announcementShown', 'true');
        setAnnouncementOpen(false);
    };

    if (authLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            {settings.motd_dashboard && (
                <Alert 
                    severity="info" 
                    sx={{ mb: 3 }}
                >
                    <div dangerouslySetInnerHTML={{ __html: settings.motd_dashboard }} />
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                </Alert>
            )}

            <DashboardPopup open={announcementOpen} onClose={handleAnnouncementClose} />

            <PlayerCoachConfirmation
                open={coachingDialogOpen}
                onClose={() => setCoachingDialogOpen(false)}
                onSuccess={handleSetCoachingPlaying}
                loading={coachingLoading}
            />

            <ContactPopup
                open={contactDialogOpen}
                onClose={() => setContactDialogOpen(false)}
            />

            {isMobile ? (
                // Mobile View
                <Stack spacing={3}>
                    <Box textAlign="center" mb={2}>
                        <Avatar
                            src={user?.memberPhotoUrl}
                            sx={{
                                width: 100,
                                height: 100,
                                margin: '0 auto',
                                mb: 2,
                                border: `4px solid ${theme.palette.primary.main}`
                            }}
                        >
                            {user?.FirstName?.[0]}{user?.LastName?.[0]}
                        </Avatar>
                        <Typography variant="h5" gutterBottom>
                            Welcome, {user?.firstName} {user?.lastName}
                        </Typography>
                        <Stack spacing={1} alignItems="center">
                            <Chip
                                icon={hasActiveMembership ? <CheckCircleIcon /> : <WarningIcon />}
                                label={getMembershipStatusText()}
                                color={hasActiveMembership ? 'success' : 'warning'}
                            />
                            {isCoachingOrPlaying ? (
                                <Chip
                                    icon={<SportsSoccerIcon />}
                                    label="Coach/Player"
                                    color="primary"
                                />
                            ) : showCoachingButton && (
                                <Button
                                    startIcon={<SportsSoccerIcon />}
                                    onClick={() => setCoachingDialogOpen(true)}
                                    variant="outlined"
                                    size="small"
                                >
                                    Coaching/Playing?
                                </Button>
                            )}
                        </Stack>
                    </Box>

                    <Stack spacing={2}>
                        {quickActions.map((action, index) => (
                            <QuickActionButton
                                key={index}
                                icon={action.icon}
                                label={action.label}
                                onClick={action.onClick}
                                color={action.color}
                                disabled={action.disabled}
                                tooltipText={action.tooltipText}
                            />
                        ))}
                    </Stack>
                </Stack>
            ) : (
                // Desktop View
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Paper 
                            elevation={2}
                            sx={{ 
                                p: 3,
                                height: '100%',
                                background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                                color: 'white'
                            }}
                        >
                            <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
                                <Avatar
                                    src={user?.memberPhotoUrl}
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        mb: 2,
                                        border: '4px solid white'
                                    }}
                                >
                                    {user?.FirstName?.[0]}{user?.LastName?.[0]}
                                </Avatar>
                                <Typography variant="h5" gutterBottom>
                                    Welcome, {user?.firstName} {user?.lastName}
                                </Typography>
                                <Chip
                                    icon={hasActiveMembership ? <CheckCircleIcon sx={{ color: '#90caf9' }} /> : <WarningIcon sx={{ color: '#ffb74d' }} />}
                                    label={getMembershipStatusText()}
                                    color={hasActiveMembership ? 'success' : 'warning'}
                                    sx={{ backgroundColor: 'white', color: 'black' }}
                                />
                                {isCoachingOrPlaying && (
                                    <Chip
                                        icon={<SportsSoccerIcon sx={{ color: '#90caf9' }} />}
                                        label="Coach/Player"
                                        sx={{ 
                                            backgroundColor: 'white', 
                                            color: 'black',
                                            mt: 1 
                                        }}
                                    />
                                )}
                                {showCoachingButton && (
                                    <Button
                                        startIcon={<SportsSoccerIcon />}
                                        onClick={() => setCoachingDialogOpen(true)}
                                        variant="outlined"
                                        size="small"
                                        sx={{ 
                                            mt: 1,
                                            backgroundColor: 'white',
                                            '&:hover': {
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)'
                                            }
                                        }}
                                    >
                                        Coaching/Playing?
                                    </Button>
                                )}
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Grid container spacing={2}>
                            {quickActions.map((action, index) => (
                                <Grid item xs={12} sm={6} key={index}>
                                    <Card>
                                        <CardContent>
                                            <QuickActionButton
                                                icon={action.icon}
                                                label={action.label}
                                                onClick={action.onClick}
                                                color={action.color}
                                                disabled={action.disabled}
                                                tooltipText={action.tooltipText}
                                            />
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            )}

            <ExternalLinkDialog
                open={dialogOpen}
                onClose={handleDialogClose}
                onConfirm={handleDialogConfirm}
                title="Administrative Fees Required"
                message={<>Your administrative fees must be paid before you can access the facility. <br/><strong>Please allow up to 5 minutes after paying fees for your status to be reflected.</strong> <br/><br/>Would you like to view the available plans?</>}
                showExternalWarning={false}
                continueButtonText="Pay Fees"
            />
        </Box>
    );
};

export default Dashboard; 